import { AKComponent } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';
import { debounce } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends AKComponent<Refs>
 */
export class MediaGallery extends AKComponent {
  /**
   * The media list container (ul element).
   *
   * @type {HTMLElement | null}
   */
  #mediaList = null;

  /**
   * The thumbnail list container (ul element).
   *
   * @type {HTMLElement | null}
   */
  #thumbnailList = null;

  #fromClick = false;

  /**
   * The intersection observer for tracking visible media items.
   *
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.#mediaList = this.refs['mediaList'] instanceof HTMLElement ? this.refs['mediaList'] : null;
    this.#thumbnailList = this.refs['thumbnailsContainer'] instanceof HTMLElement ? this.refs['thumbnailsContainer'] : null;
    // Initialise l'observateur d'intersection pour détecter les changements de position
    this.#setupIntersectionObserver();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cleanupIntersectionObserver();
  }

  /**
   * Sets up the intersection observer to track visible media items.
   */
  #setupIntersectionObserver() {
    if (!this.#mediaList) return;

    // Nettoyer l'observateur existant s'il y en a un
    this.#cleanupIntersectionObserver();

    // Récupérer tous les éléments li
    const mediaItems = Array.from(this.#mediaList.querySelectorAll('li'));
    if (!mediaItems.length) return;

    // Configuration de l'IntersectionObserver
    const options = {
      root: this.#mediaList, // Le conteneur de scroll
      rootMargin: '0px',
      threshold: 0.5 // L'élément doit être au moins 50% visible
    };

    // Fonction debounced pour éviter trop d'appels
    const debouncedCallback = debounce((visibleElement) => {
      const currentIndex = mediaItems.indexOf(visibleElement);
      if (currentIndex !== -1) {
        this.#setActiveThumbnail(currentIndex);
      }
    }, 100);

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      // Trouver l'élément qui devient visible (il n'y en a qu'un à la fois avec CSS snap)
      const visibleEntry = entries.find(entry => entry.isIntersecting);
      
      if (visibleEntry) {
        debouncedCallback(visibleEntry.target);
      }
    }, options);

    // Observer tous les éléments li
    mediaItems.forEach(item => {
      this.#intersectionObserver?.observe(item);
    });
  }

  /**
   * Cleans up the intersection observer.
   */
  #cleanupIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };
  /**
   * Handles a thumbnail click event by selecting the corresponding media.
   *
   * @param {number} index - The index of the media to select.
   */
  handleThumbnailClick(index) {
    this.#fromClick = true;
    if (!this.#mediaList) {
      return;
    }

    // Récupère tous les éléments li enfants
    const mediaItems = Array.from(this.#mediaList.querySelectorAll('li'));
    
    if (index < 0 || index >= mediaItems.length) {
      return;
    }

    // Trouve l'élément li correspondant à l'index
    const targetItem = mediaItems[index];
    
    if (targetItem) {
      // ajout de la class active au thumbnail correspondant
      // et suppression de la class active de tous les autres thumbnails
      if (this.#thumbnailList) {
        const thumbnails = this.#thumbnailList.querySelectorAll('button');
        thumbnails.forEach(thumbnail => {
          thumbnail.classList.remove('c-media__thumb--active');
        });
        const thumbnail = this.#thumbnailList.querySelector(`button[data-index="${index}"]`);
        if (thumbnail instanceof HTMLElement) {
          thumbnail.classList.add('c-media__thumb--active');
          this.#scrollThumbnailIntoView(thumbnail);
        }
      }

      // Fait défiler vers l'élément cible en utilisant scrollIntoView
      // avec smooth behavior pour une transition fluide
      targetItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      
      // Remettre fromClick à false une fois le scroll terminé
      this.#handleScrollEnd();
    }
  };

  /**
   * Scrolls the thumbnail into view so it is the leftmost visible element in the thumbnails slider.
   * @param {HTMLElement} thumbnail - The thumbnail element to scroll into view.
   */
  #scrollThumbnailIntoView(thumbnail) {
    if (!thumbnail || !this.#thumbnailList) return;
    const scrollableThumbnails =
      this.#thumbnailList.querySelector('.c-media__thumbnails') ?? this.#thumbnailList.firstElementChild;
    if (!(scrollableThumbnails instanceof HTMLElement)) return;
    scrollIntoView(thumbnail, {
      ancestor: scrollableThumbnails,
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  /**
   * Sets the active thumbnail based on the current media index.
   * @param {number} index - The index of the active media item.
   */
  #setActiveThumbnail(index) {
    if (!this.#thumbnailList || this.#fromClick) return;
    const thumbnails = this.#thumbnailList.querySelectorAll('button');
    thumbnails.forEach(thumbnail => {
      thumbnail.classList.remove('c-media__thumb--active');
    });
    const thumbnail = this.#thumbnailList.querySelector(`button[data-index="${index}"]`);
    if (thumbnail instanceof HTMLElement) {
      thumbnail.classList.add('c-media__thumb--active');
      this.#scrollThumbnailIntoView(thumbnail);
    }
  }

  /**
   * Handles the end of scroll animation to reset fromClick flag.
   */
  #handleScrollEnd() {
    if (!this.#mediaList) return;

    // Utiliser l'événement scrollend si supporté, sinon fallback avec setTimeout
    if ('onscrollend' in this.#mediaList) {
      const handleScrollEnd = () => {
        this.#fromClick = false;
        this.#mediaList?.removeEventListener('scrollend', handleScrollEnd);
      };
      this.#mediaList.addEventListener('scrollend', handleScrollEnd, { once: true });
    } else {
      // Fallback pour les navigateurs qui ne supportent pas scrollend
      // Estimer la durée du scroll smooth (généralement 300-500ms)
      setTimeout(() => {
        this.#fromClick = false;
      }, 500);
    }
  }

}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
